import { useCallback, useMemo } from 'react';
import { EnquiryFormTrigger } from 'generated/graphql';

import {
    useOpenBookMeetingGate,
    type BookMeetingGateIntent,
} from '~/modules-rocco/booking/book-meeting-gate-state';
import { usePageCtas } from '../component/PageCtasProvider';
import { useEnquiryModal, type EnquiryModalContext } from './use-enquiry-modal';
import type { PageCtas } from '../domain/page-ctas';

type OpenEnquiryArgs = NonNullable<
    Parameters<ReturnType<typeof useEnquiryModal>['openEnquiryModal']>[0]
>;

/** What one surface adds to the page's enquiry context. */
export interface EnquireCtaOptions {
    /** Reported with the reveal and engagement events. */
    source?: EnquiryModalContext['source'];
    /**
     * Surface copy that stays put while the flat settings decide (e.g. a
     * "Send Enquiry" button). A configured form's label replaces it.
     */
    fixedLabel?: string;
    itemTitle?: string;
    itemImage?: string;
    brandImage?: string;
}

export interface EnquireCta {
    visible: boolean;
    showSpecifierLoginPrompt: boolean;
    label: string;
    labelShort: string;
    /** Open the enquiry modal; returns false when the button is switched off. */
    open: (extra?: OpenEnquiryArgs) => boolean;
}

/**
 * The page's Enquire button for one surface: whether it shows, what it says,
 * and an `open` that sends the modal this page's form.
 */
export const useEnquireCta = ({
    source,
    fixedLabel,
    itemTitle,
    itemImage,
    brandImage,
}: EnquireCtaOptions = {}): EnquireCta => {
    const { ctas, enquiry } = usePageCtas();
    const cta = ctas.enquire;
    const data = useMemo<EnquiryModalContext>(
        () => ({
            ...enquiry,
            ...(source && { source }),
            ...(itemTitle && { itemTitle }),
            ...(itemImage && { itemImage }),
            ...(brandImage && { brandImage }),
        }),
        [enquiry, source, itemTitle, itemImage, brandImage],
    );
    // The page's `CtaAutoOpen` owns the `enquire` URL param.
    const { openEnquiryModal } = useEnquiryModal(data, {
        autoOpenFromUrl: false,
    });
    const label = cta.usesForms ? cta.label : (fixedLabel ?? cta.label);
    const labelShort = cta.usesForms
        ? cta.labelShort
        : (fixedLabel ?? cta.labelShort);

    const open = useCallback(
        (extra: OpenEnquiryArgs = {}) =>
            openEnquiryModal({
                title: label,
                ctaLabel: label,
                trigger: EnquiryFormTrigger.Enquire,
                formConfig: cta.formConfig,
                ...extra,
            }),
        [openEnquiryModal, label, cta.formConfig],
    );

    return {
        visible: cta.visible,
        showSpecifierLoginPrompt: cta.showSpecifierLoginPrompt,
        label,
        labelShort,
        open,
    };
};

/**
 * The practice a Book button books with, when it is not the page's own (a
 * project's team tile crediting another practice).
 */
export interface BookCtaProfessional {
    urlSegment: string;
    id?: number;
    name?: string;
    logo?: string | null;
    logoBackgroundColor?: string | null;
}

/** Who and what one Book button books; set per surface or per click. */
export interface BookCtaTarget {
    /** A team member's card: open on them. */
    preferredTeamMemberId?: number;
    preferredTeamMemberName?: string;
    /**
     * That person's own booking form. It replaces the page's form, so the
     * page form's questions do not run first.
     */
    bookingFormId?: number;
    /** Sessions this button books into instead of the page's. */
    sessionIds?: number[];
    paneImage?: string;
    expectsTeamPicker?: boolean;
    professional?: BookCtaProfessional;
}

/** What one surface adds to the page's booking intent. */
export interface BookCtaOptions extends BookCtaTarget {
    source?: EnquiryModalContext['source'];
    /** See {@link EnquireCtaOptions.fixedLabel}. */
    fixedLabel?: string;
}

export interface BookCta {
    visible: boolean;
    /** Show beside the primary enquiry CTA; see {@link PageBookCta.inline}. */
    inline: boolean;
    label: string;
    labelShort: string;
    /** Open the gate (or the enquiry modal standing in for it). */
    open: () => void;
    /**
     * {@link open}, with `target` added to the surface's own options, for
     * buttons that only know who they book on click, such as a card in a team
     * carousel. Separate from `open` so a click event handed straight to
     * `onClick` can never be read as a target.
     */
    openFor: (target: BookCtaTarget) => void;
}

const professionalIntent = (professional: BookCtaProfessional | undefined) =>
    professional
        ? {
              professionalUrlSegment: professional.urlSegment,
              professionalId: professional.id,
              professionalName: professional.name,
              companyLogo: professional.logo,
              logoBackgroundColor: professional.logoBackgroundColor,
          }
        : {};

/** The part of the intent that says who is booked and on which form. */
const targetIntent = (
    target: BookCtaTarget,
    page: PageCtas['book'],
): Partial<BookMeetingGateIntent> => ({
    ...professionalIntent(target.professional),
    sessionIds: target.sessionIds ?? page.sessionIds,
    ...(target.bookingFormId !== undefined
        ? { bookingFormId: target.bookingFormId }
        : {
              formId: page.formId,
              formConfig: page.formConfig,
              detailsFirst: page.detailsFirst,
          }),
    ...(target.preferredTeamMemberId !== undefined && {
        preferredTeamMemberId: target.preferredTeamMemberId,
        preferredTeamMemberName: target.preferredTeamMemberName,
    }),
    ...(target.paneImage && { paneImage: target.paneImage }),
    ...(target.expectsTeamPicker !== undefined && {
        expectsTeamPicker: target.expectsTeamPicker,
    }),
});

/**
 * The page's Book a meeting button for one surface. `open` goes to the booking
 * gate with the page's form, sessions and details-first flag; a personal
 * `bookingFormId` replaces the page form. With the gate switched off the
 * enquiry modal stands in, carrying the Book a meeting form.
 */
export const useBookCta = ({
    source,
    fixedLabel,
    ...surfaceTarget
}: BookCtaOptions = {}): BookCta => {
    const { ctas, enquiry, book } = usePageCtas();
    const cta = ctas.book;
    const openGate = useOpenBookMeetingGate();
    const data = useMemo<EnquiryModalContext>(
        () => ({ ...enquiry, ...(source && { source }) }),
        [enquiry, source],
    );
    // The page's `CtaAutoOpen` owns the `enquire` and `book` URL params.
    const { openEnquiryModal } = useEnquiryModal(data, {
        autoOpenFromUrl: false,
    });
    const label = cta.usesForms ? cta.label : (fixedLabel ?? cta.label);
    const labelShort = cta.usesForms
        ? cta.labelShort
        : (fixedLabel ?? cta.labelShort);
    // Options arrive as a fresh object each render; key the callback on their
    // content so `open` keeps its identity.
    const surfaceKey = JSON.stringify(surfaceTarget);

    const openFor = useCallback(
        (target: BookCtaTarget) => {
            if (!cta.viaGate) {
                openEnquiryModal({
                    title: label,
                    ctaLabel: label,
                    trigger: EnquiryFormTrigger.BookMeeting,
                    formConfig: cta.formConfig,
                });
                return;
            }
            const surface = JSON.parse(surfaceKey) as BookCtaTarget;
            const merged = { ...surface, ...target };
            openGate({
                ...book,
                pageType: ctas.pageType,
                ...(merged.preferredTeamMemberId === undefined && {
                    ctaLabel: label,
                }),
                ...targetIntent(merged, cta),
            });
        },
        [
            cta,
            ctas.pageType,
            book,
            label,
            surfaceKey,
            openGate,
            openEnquiryModal,
        ],
    );

    const open = useCallback(() => openFor({}), [openFor]);

    return {
        visible: cta.visible,
        inline: cta.inline,
        label,
        labelShort,
        open,
        openFor,
    };
};
